import axios from 'axios';
import type { ReactElement, ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';
import {
  fetchMaintenanceStatus,
  isMaintenanceError,
} from '../../helper/maintenanceHelper';
import { FullScreenLoading } from '../Loading/FullScreenLoading';
import { MaintenancePage } from './MaintenancePage';

const CHECK_INTERVAL_MS = 30_000;

type GateState = 'checking' | 'maintenance' | 'available';

interface MaintenanceGateProps {
  children: ReactNode;
}

// Shows the maintenance page instead of the application while the API is in maintenance mode, which is switched with
// the MaintenanceMode setting of the function app. It sits in front of the application, so nothing of it (sign-in,
// requests to the API) starts while the database is being worked on.
export const MaintenanceGate = ({
  children,
}: MaintenanceGateProps): ReactElement => {
  const [state, setState] = useState<GateState>('checking');

  // is the application in maintenance mode when it is opened
  useEffect(() => {
    let cancelled = false;

    fetchMaintenanceStatus().then((maintenance) => {
      if (!cancelled) {
        setState(maintenance ? 'maintenance' : 'available');
      }
    });

    return (): void => {
      cancelled = true;
    };
  }, []);

  // the maintenance started while the application is in use: the next request of the user is refused with 503
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error: unknown) => {
        if (isMaintenanceError(error)) {
          setState('maintenance');
        }
        return Promise.reject(error);
      }
    );

    return (): void => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, []);

  const checkAvailability = useCallback(async (): Promise<void> => {
    if (!(await fetchMaintenanceStatus())) {
      setState('available');
    }
  }, []);

  // when the maintenance is over the application opens by itself
  useEffect(() => {
    if (state !== 'maintenance') {
      return undefined;
    }

    const timer = window.setInterval(() => {
      void checkAvailability();
    }, CHECK_INTERVAL_MS);

    return (): void => {
      window.clearInterval(timer);
    };
  }, [state, checkAvailability]);

  if (state === 'checking') {
    return <FullScreenLoading />;
  }

  if (state === 'maintenance') {
    return <MaintenancePage />;
  }

  return <>{children}</>;
};
